import { type JSX, useCallback, useMemo } from "react";
import styled from "styled-components";
import { useMutation } from "urql";
import { currentColors } from "../../shared/colors";
import t from "../../shared/translations";
import { useRefetchUnreadMessages } from "../components/UnreadMessages";
import { deleteMutation, type IMessage, toggleArchiveMutataion, toggleReadMutation } from "./query";

interface IProps {
    messages: IMessage[];
    onDone: () => void;
}

const Wrapper = styled.div`
    display: inline-flex;
    gap: 12px;
    align-items: center;
    margin-bottom: 0.5rem;
`;

const ActionLink = styled.span`
    color: ${currentColors.link};
    cursor: pointer;

    &:hover {
        color: ${currentColors.link};
    }
`;

function MessagesActions({ messages, onDone }: IProps): JSX.Element {
    const [deleteResult, executeDelete] = useMutation(deleteMutation);
    const [archiveResult, executeArchive] = useMutation(toggleArchiveMutataion);
    const [readResult, executeRead] = useMutation(toggleReadMutation);
    const refetchUnread = useRefetchUnreadMessages();

    const { ids, showArchive, showMarkRead, showMarkUnread } = useMemo(() => {
        const ids: string[] = [];
        let showArchive = false;
        let showMarkRead = false;
        let showMarkUnread = false;
        for (const message of messages) {
            ids.push(message.id);
            if (!message.archived) {
                showArchive = true;
            }
            if (!message.read) {
                showMarkRead = true;
            }
            if (message.read) {
                showMarkUnread = true;
            }
        }
        return { ids, showArchive, showMarkRead, showMarkUnread };
    }, [messages]);

    const archive = useCallback(async () => {
        if (archiveResult.fetching || ids.length === 0) {
            return;
        }
        await executeArchive({ ids, archived: true });
        onDone();
    }, [archiveResult.fetching, executeArchive, ids, onDone]);

    const markRead = useCallback(async () => {
        if (readResult.fetching || ids.length === 0) {
            return;
        }
        await executeRead({ ids, read: true });
        refetchUnread?.();
        onDone();
    }, [executeRead, ids, onDone, readResult.fetching, refetchUnread]);

    const markUnread = useCallback(async () => {
        if (readResult.fetching || ids.length === 0) {
            return;
        }
        await executeRead({ ids, read: false });
        refetchUnread?.();
        onDone();
    }, [executeRead, ids, onDone, readResult.fetching, refetchUnread]);

    const del = useCallback(async () => {
        if (deleteResult.fetching || ids.length === 0) {
            return;
        }
        if (!window.confirm(t("player.messages.confirm-delete-selected"))) {
            return;
        }
        await executeDelete({ ids });
        refetchUnread?.();
        onDone();
    }, [deleteResult.fetching, executeDelete, ids, onDone, refetchUnread]);

    if (messages.length === 0) {
        return null;
    }

    return (
        <Wrapper>
            {showArchive && <ActionLink onClick={archive}>{t("player.messages.actions-archive")}</ActionLink>}
            {showMarkRead && <ActionLink onClick={markRead}>{t("player.messages.actions-mark-read")}</ActionLink>}
            {showMarkUnread && <ActionLink onClick={markUnread}>{t("player.messages.actions-mark-unread")}</ActionLink>}
            <ActionLink onClick={del}>{t("player.messages.actions-delete")}</ActionLink>
        </Wrapper>
    );
}

export default MessagesActions;
